// Implements: F-PRV-001
import { useEffect } from "react";
import type { ReactElement, Ref } from "react";

import { isFrameDocumentPath } from "../frameDocumentPath";
import type { FrameDocumentPath } from "../frameDocumentPath";
import styles from "./SandboxedFrame.module.css";

/**
 * The isolation of AD-04, written once and never parameterised.
 *
 * `allow-same-origin` is absent on purpose. Granted next to `allow-scripts`,
 * it lets the embedded document reach into its own frame element and remove
 * the sandbox, which is the one combination that turns this attribute into
 * decoration (research C17). A default value on a `sandbox` prop would be a
 * value somebody can replace, so there is no such prop.
 */
const SANDBOX = "allow-scripts allow-forms";

export interface SandboxedFrameProps {
  readonly src: FrameDocumentPath;
  readonly title: string;
  readonly className?: string | undefined;
  /** The frame element, for the relay listener that compares `event.source` with its window. */
  readonly frameRef?: Ref<HTMLIFrameElement> | undefined;
  /** Called once when `src` is not a frame-document path and nothing is rendered. */
  readonly onRefused?: (() => void) | undefined;
}

/**
 * The only `<iframe>` in the application (AD-04, design DD14).
 *
 * The document arrives by `src`, from the authenticated frame route, and never
 * through `srcdoc`. The frame still runs in an opaque origin because the
 * attribute has no `allow-same-origin`: it cannot read the app's cookies or
 * storage, and cannot navigate the top window. `src` is accepted only when it is
 * a same-origin frame-document path built by `previewDocumentPath`; anything
 * else (an absolute or protocol-relative URL, `javascript:`, `data:`, `blob:`)
 * renders nothing and is reported through `onRefused`.
 */
export function SandboxedFrame({
  src,
  title,
  className,
  frameRef,
  onRefused,
}: SandboxedFrameProps): ReactElement | null {
  const accepted = isFrameDocumentPath(src);
  useEffect(() => {
    if (!accepted) onRefused?.();
  }, [accepted, src, onRefused]);
  if (!accepted) return null;

  const classNames = [styles.frame, className]
    .filter((entry): entry is string => entry !== undefined)
    .join(" ");

  return (
    <iframe
      ref={frameRef}
      className={classNames}
      title={title}
      src={src}
      sandbox={SANDBOX}
      referrerPolicy="no-referrer"
      allow=""
    />
  );
}
